import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { makeStyles } from "@material-ui/core/styles"

import {
  Dialog,
  CircularProgress,
  Typography,
  IconButton,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  DialogTitle,
  DialogContent,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'

import { Cancel } from '@material-ui/icons'
import { styled } from '@mui/material/styles'
import TextField from "@material-ui/core/TextField"
import Pagination from "@material-ui/lab/Pagination"

import DeleteIcon from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import CheckIcon from '@material-ui/icons/Check'

const useStyles = makeStyles((theme) => ({
    tableStyle: {
      border: "none",
      boxShadow: "none",
    },
    tableText: {
      fontFamily: "Open Sans",
      fontWeight: "normal",
      fontSize: "12px",
      lineHeight: "16px",
      color: "#141414",
      textAlign: "center",
      margin: "10px",
      padding: "6px",
      whiteSpace: "nowrap"
    },
    tableLinkText: {
      fontFamily: "Open Sans",
      fontWeight: "normal",
      fontSize: "12px",
      lineHeight: "16px",
      color: "#0b1f82",
      textAlign: "center",
      margin: "10px",
      padding: "6px",
      whiteSpace: "nowrap"
    },
    tableHeader: {
      textAlign: "center",
      fontFamily: "Open Sans",
      fontStyle: "normal",
      fontWeight: "normal",
      color: "#6F6F6F",
      fontSize: "14px",
      lineHeight: "19px",
      padding: "6px",
    },
    even: { background: "#F3F3F3" },
    odd: { background: "#FFFFFF" },
    paginationContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: "10px",
    },
    actionCell: { width: "50px", padding: "0px 8px" },
    badge: {
        borderRadius: "4px",
        padding: "2px 8px",
        fontSize: "14px",
        display: "inline-block",
        cursor: "help",
        textAlign: "center",
        minWidth: "30px",
    },
    badgeGreen: {
        backgroundColor: "#6dd67d55",
        color: "#00C41D",
        fontWeight: "bold"
    },
    badgeRed: {
        backgroundColor: "#ff828255",
        color: "#A60505",
        fontWeight: "bold",
    },
}))

const SectionHeader = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(3),
    borderBottom: `2px solid ${theme.palette.divider}`,
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px'
}))

function BatchOccurrencesTable({ data, onAction, getActionIcon, emptyMessage, paginationState, setOccurrenceDialogState }) {
    const classes = useStyles()
    const { t } = useTranslation()

    const getRowClass = (index) => (index % 2 === 0 ? classes.even : classes.odd)

    const openViewOccurrenceDialog = (occurrence) => {
        if(!occurrence.occurrence_key) return
        setOccurrenceDialogState({
            open: true,
            mode: "view",
            occurrence: {
                occurrence_key: occurrence.occurrence_key,
                status: "in_batch"
            }
        })
    }

    const renderStatusBadge = (statusRaw) => {
        let badgeClass = classes.badge
        
        if (statusRaw === 'in_batch') {
            badgeClass += ` ${classes.badgeGreen}`
        } else if (statusRaw === 'out_of_batch') {
            badgeClass += ` ${classes.badgeRed}`
        } else {
            return t(statusRaw)
        }

        return (
            <span className={badgeClass}>
                {t(statusRaw)}
            </span>
        )
    }

    return (
        <div>
            <Paper className={classes.tableStyle}>
                <Table style={{ padding: "8px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeader}>{t("Nome da ocorrência")}</TableCell>
                            <TableCell className={classes.tableHeader}>{t("Status")}</TableCell>
                            <TableCell className={classes.tableHeader}>{t("Data da Criação")}</TableCell>
                            <TableCell className={classes.tableHeader}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!data || data.length === 0) ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <div className="emptyContainer" style={{ padding: "20px", textAlign: "center" }}>
                                        <p className={["blueText", "subtitleSize"].join(" ")}>{t(emptyMessage)}</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((occurrence, index) => (
                                <TableRow key={occurrence.occurrence_key || index} className={getRowClass(index)}>
                                    <TableCell
                                        className={["link", "subtitleSize", "bold"].join(" ")}
                                        style={{color: "#0b1f82", cursor: "pointer"}}
                                        onClick={() => openViewOccurrenceDialog(occurrence)}
                                    >
                                            {occurrence.external_id || occurrence.occurrence_external_id}
                                    </TableCell>
                                    <TableCell className={classes.tableText}>
                                        {renderStatusBadge(occurrence.status)}
                                    </TableCell>
                                    <TableCell className={classes.tableText}>
                                        {moment(occurrence.created_at).format("DD/MM/YYYY HH:mm:ss")}
                                    </TableCell>
                                    <TableCell className={classes.actionCell}>
                                        {getActionIcon ? getActionIcon(occurrence) : (
                                            <IconButton size="small" onClick={() => onAction(occurrence)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {data && data.length > 0 && paginationState && (
                <div className={classes.paginationContainer}>
                    <Pagination
                        className="pagination"
                        count={paginationState.count}
                        page={paginationState.page}
                        onChange={paginationState.onChange}
                    />
                </div>
            )}
        </div>
    )
}

function CoafBatchDialog(props) {
    const { t } = useTranslation()

    const { batch, batchDialogState, setBatchDialogState, setOccurrenceDialogState, refreshList, setSnackbarContent } = props
    const { open, mode } = batchDialogState

    const [batchData, setBatchData] = useState({ occurrence_batch_external_id: "", occurrences: [], regulatory_authority: "bacen" })
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [searchResults, setSearchResults] = useState([])
    const [searchPage, setSearchPage] = useState(1)
    const [searchTotalPages, setSearchTotalPages] = useState(0)
    const [isSearching, setIsSearching] = useState(false)

    const [confirmExitOpen, setConfirmExitOpen] = useState(false)

    const [currentPage, setCurrentPage] = useState(1)
    const pageSize = 5

    const trimNestedObject = (input) => {
        const isPlainObject = (val) => (
            val !== null &&
            Object.prototype.toString.call(val) === "[object Object]" &&
            (Object.getPrototypeOf(val) === Object.prototype || Object.getPrototypeOf(val) === null)
        )

        if (!(Array.isArray(input) || isPlainObject(input))) return input

        const root = Array.isArray(input) ? [] : {}
        const stack = [{ source: input, target: root }]

        const assignValue = (container, key, value) => {
            if (typeof value === "string") {
                container[key] = value.trim()
            } else if (Array.isArray(value)) {
                const next = []
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (isPlainObject(value)) {
                const next = {}
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (value === null || value === undefined) {
                delete container[key]
            } else {
                container[key] = value
            }

        }

        while (stack.length) {
            const { source, target } = stack.pop()

            if (Array.isArray(source)) {
                source.forEach((value, index) => assignValue(target, index, value))
            } else {
                Object.entries(source).forEach(([key, value]) => assignValue(target, key, value))
            }
        }

        return root
    }

    const handleAttemptClose = () => {
        if (mode === 'view') {
            handleClose()
        } else {
            setConfirmExitOpen(true)
        }
    }

    const handleClose = () => {
        setBatchDialogState({ open: false, mode: "view", batch: null })
        setBatchData({ occurrence_batch_external_id: "", occurrences: [], regulatory_authority: "bacen" })
        setIsSearchExpanded(false)
        setSearchQuery("")
        setSearchResults([])
        if (refreshList) refreshList()
    }

    const fetchBatchData = useCallback(async (batch_key) => {
        setIsLoading(true)
        try {
            let response = await axios.get(`/dash/alerts/occurrence_batch/${batch_key}`)
            setBatchData({
                ...response.data,
                regulatory_authority: response.data.regulatory_authority || "bacen"
            })
        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao buscar os dados do lote. Por favor, tente novamente."
            })
        } finally {
            setIsLoading(false)
        }
    }, [setSnackbarContent])

    useEffect(() => {
        if (!open) return
        if (mode === "create") {
            setBatchData({ occurrence_batch_external_id: "", occurrences: [], regulatory_authority: "bacen" })
        } else {
            fetchBatchData(batch.occurrence_batch_key)
        }
    }, [open, mode, batch, fetchBatchData])

    const handleRemoveOccurrence = (occurrenceToRemove) => {
        setBatchData(prev => ({
            ...prev,
            occurrences: prev.occurrences.filter(o => o.occurrence_key !== occurrenceToRemove.occurrence_key)
        }))
    }

    const handleAddOccurrence = (occurrenceToAdd) => {
        setBatchData(prev => ({
            ...prev,
            occurrences: [occurrenceToAdd, ...prev.occurrences]
        }))
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            const payload = {
                occurrence_batch_data: {
                    occurrence_batch_external_id: batchData.occurrence_batch_external_id,
                    occurrence_keys: batchData.occurrences.map(o => o.occurrence_key),
                    regulatory_authority: batchData.regulatory_authority || "bacen"
                }
            }

            if (mode === 'create') {
                await axios.post(`/dash/alerts/occurrence_batch`, trimNestedObject({occurrence_batch_data: {...payload.occurrence_batch_data, occurrence_batch_type: 'communication'}}))
            } else {
                await axios.put(`/dash/alerts/occurrence_batch/${batch.occurrence_batch_key}`, trimNestedObject(payload))
            }

            handleClose()
        } catch (error) {
            if (error.response.status === 409 && error.response.data.title === "Duplicate occurrence batch external_id") {
                setSnackbarContent({
                    type: "error",
                    message: "Já existe um lote com o mesmo identificador. Por favor, utilize um identificador único."
                })
            } else {
                setSnackbarContent({
                    type: "error",
                    message: "Ocorreu um erro ao salvar o lote. Por favor, tente novamente."
                })
            }
        } finally {
            setIsSaving(false)
        }
    }

    const searchOccurrences = useCallback(async (page, query) => {
        setIsSearching(true)
        try {
            const authority = batchData.regulatory_authority || "bacen"
            const params = {
                page_number: page - 1,
                page_rows: 5,
                status: 'out_of_batch',
                regulatory_authority: authority
            }
            if (query) params.link_key = query

            const response = await axios.get("/dash/alerts/occurrence", { params })
            const allFetched = response.data.occurrences || []
            const matchingAuthority = allFetched.filter(occ => (occ.regulatory_authority || "bacen") === authority)

            setSearchResults(matchingAuthority)
            setSearchTotalPages(response.data.number_of_pages)
        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao buscar as ocorrências. Por favor, tente novamente."
            })
        } finally {
            setIsSearching(false)
        }
    }, [setSnackbarContent, batchData.regulatory_authority])

    useEffect(() => {
        if (isSearchExpanded) {
            const timeoutId = setTimeout(() => {
                searchOccurrences(searchPage, searchQuery)
            }, 500)
            return () => clearTimeout(timeoutId)
        }
    }, [searchPage, searchQuery, isSearchExpanded, searchOccurrences])

    const isOccurrenceInBatch = (occurrenceKey) => {
        return batchData.occurrences.some(o => o.occurrence_key === occurrenceKey)
    }

    const renderSearchActionIcon = (occ) => {
        const inBatch = isOccurrenceInBatch(occ.occurrence_key)
        if (inBatch) {
             return (
                <Tooltip title={t("Já adicionado")}>
                    <span>
                        <IconButton size="small" disabled>
                            <CheckIcon style={{ color: "#bdbdbd" }} />
                        </IconButton>
                    </span>
                </Tooltip>
             )
        }
        return (
            <Tooltip title={t("Adicionar")}>
                <IconButton size="small" onClick={() => handleAddOccurrence(occ)}>
                    <AddIcon style={{ color: "green" }} />
                </IconButton>
            </Tooltip>
        )
    }

    const modeVerb = mode === "create" ? t("Criar") : mode === "edit" ? t("Editar") : t("Visualizar")
    const readOnly = mode === "view"

    const currentListStartIndex = (currentPage - 1) * pageSize
    const currentListEndIndex = currentPage * pageSize
    const currentListSliced = batchData.occurrences ? batchData.occurrences.slice(currentListStartIndex, currentListEndIndex) : []

    return (
        <>
        <Dialog
            open={open}
            onClose={handleAttemptClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ style: { padding: "20px 25px" } }}
        >
            <div style={{ display: "flex", marginBottom: "12px", justifyContent: "space-between" }}>
                <span className={["blueText", "titleSize"].join(" ")}>
                    {[modeVerb, t("Lote")].join(' ')}
                </span>
                <IconButton onClick={handleAttemptClose}>
                    <Cancel />
                </IconButton>
            </div>

            {isLoading ? (
                <div className='circularProgressCenter'>
                    <CircularProgress />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                    <SectionHeader>
                        <Typography className="blueText" style={{ fontWeight: 'bold' }}>
                            {t("Dados do Lote")}
                        </Typography>
                    </SectionHeader>

                    <div style={{ display: "flex", alignItems: "flex-end", gap: "24px", flexWrap: "wrap" }}>
                        <TextField
                            label={t("Nome do Lote")}
                            value={batchData.occurrence_batch_external_id || ""}
                            onChange={(e) => setBatchData({ ...batchData, occurrence_batch_external_id: e.target.value })}
                            InputProps={{ readOnly: readOnly }}
                            style={{ flex: 1, minWidth: "200px" }}
                        />
                        <div style={{ minWidth: "140px" }}>
                            <Typography variant="body2" className="blueText" style={{ marginBottom: "6px", fontWeight: 500}}>
                                {t("regulatory_authority")}
                            </Typography>
                            <ToggleButtonGroup
                                value={batchData.regulatory_authority === "cvm" ? "cvm" : "bacen"}
                                exclusive
                                onChange={(_, newAuthority) => {
                                    if (newAuthority == null) return
                                    setBatchData(prev => ({
                                        ...prev,
                                        regulatory_authority: newAuthority,
                                        ...(prev.regulatory_authority !== newAuthority ? { occurrences: [] } : {})
                                    }))
                                }}
                                size="small"
                                disabled={mode !== "create"}
                                sx={{
                                    backgroundColor: "rgba(11, 31, 130, 0.12)",
                                    borderRadius: "50px",
                                    padding: "4px",
                                    "&.Mui-disabled": { opacity: 1 },
                                    "& .MuiToggleButtonGroup-grouped": {
                                        border: "none",
                                        "&:not(:first-of-type)": { border: "none", marginLeft: 0 },
                                    },
                                    "& .MuiToggleButton-root": {
                                        borderRadius: "50px",
                                        border: "none",
                                        color: "#0b1f82",
                                        fontWeight: 500,
                                        minWidth: "72px",
                                        transition: "background-color 200ms ease-in-out, color 200ms ease-in-out",
                                        "&.Mui-selected": {
                                            backgroundColor: "#0b1f82",
                                            color: "#fff",
                                            "&:hover": { backgroundColor: "#0b1f82" },
                                        },
                                        "&:not(.Mui-selected):hover": {
                                            backgroundColor: "rgba(11, 31, 130, 0.08)",
                                        },
                                    },
                                    "& .MuiToggleButton-root.Mui-disabled": {
                                        border: "none !important",
                                        outline: "none !important",
                                        boxShadow: "none !important",
                                    },
                                    "& .MuiToggleButton-root.Mui-disabled:not(.Mui-selected)": {
                                        color: "rgba(0, 0, 0, 0.38)",
                                    },
                                }}
                            >
                                <ToggleButton value="bacen" disableRipple>{t("BACEN")}</ToggleButton>
                                <ToggleButton value="cvm" disableRipple>{t("CVM")}</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>

                    <SectionHeader>
                        <Typography className="blueText" style={{ fontWeight: 'bold' }}>
                            {t("Ocorrências no Lote")}
                        </Typography>
                    </SectionHeader>

                    <BatchOccurrencesTable
                        data={currentListSliced}
                        setOccurrenceDialogState={setOccurrenceDialogState}
                        emptyMessage="Este lote está vazio."
                        getActionIcon={(occ) => !readOnly && (
                            <Tooltip title={t("Remover")}>
                                <IconButton size="small" onClick={() => handleRemoveOccurrence(occ)}>
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                        paginationState={{
                            count: Math.ceil((batchData.occurrences?.length || 0) / pageSize),
                            page: currentPage,
                            onChange: (e, p) => setCurrentPage(p)
                        }}
                    />

                    {!readOnly && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                <Button
                                    disableRipple
                                    startIcon={isSearchExpanded ? <Cancel /> : <AddIcon />}
                                    sx={{color: "#0b1f82"}}
                                    onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                                    style={{ textTransform: 'none', fontWeight: 'bold' }}
                                >
                                    {isSearchExpanded ? t("Fechar busca") : t("Adicionar Ocorrência Existente")}
                                </Button>
                            </div>

                            {isSearchExpanded && (
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                        <TextField
                                            label={t("Buscar por Nome da Ocorrência")}
                                            value={searchQuery}
                                            onChange={(e) => { setSearchQuery(e.target.value); setSearchPage(1) }}
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                endAdornment: <SearchIcon color="action" />
                                            }}
                                            className="filterTextbox"
                                        />
                                    </div>

                                    {isSearching ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                                            <CircularProgress size={20} />
                                        </div>
                                    ) : (
                                        <BatchOccurrencesTable
                                            data={searchResults}
                                            setOccurrenceDialogState={setOccurrenceDialogState}
                                            emptyMessage={t("Nenhuma ocorrência encontrada.")}
                                            getActionIcon={renderSearchActionIcon}
                                            paginationState={{
                                                count: searchTotalPages,
                                                page: searchPage,
                                                onChange: (e, p) => setSearchPage(p)
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                                <Button
                                    variant="contained"
                                    className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    style={{ minWidth: '150px',  borderRadius: '18px' }}
                                >
                                    {isSaving ? <CircularProgress size={24} color="inherit" /> : t("Confirmar dados")}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Dialog>
        {confirmExitOpen && <Dialog
            open={confirmExitOpen}
            onClose={() => setConfirmExitOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title" style={{ textAlign: "center", color: "#0b1f82", fontWeight: "bold" }}>
                {t("Tem certeza que deseja fechar?")}
            </DialogTitle>
            <DialogContent>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "20px"
                    }}
                >
                    <div className="internalCardContainer normalText dialogInfoSize" id="alert-dialog-description" style={{ fontWeight: "normal", textAlign: "justify" }}>
                        {t("Você perderá os dados novos/alterados. Deseja continuar?")}
                    </div>
                    <div style={{ display: "flex", gap: "15px" }}>
                        <Button
                            onClick={() => setConfirmExitOpen(false)}
                            variant="outlined"
                            sx={{
                                color: "#0b1f82",
                                borderColor: "#0b1f82",
                                "&:hover": { borderColor: "#0b1f82", backgroundColor: "rgba(11, 31, 130, 0.08)" }
                            }}
                        >
                            {t("Não, continuar editando")}
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{
                                backgroundColor: "#0b1f82",
                                "&:hover": { backgroundColor: "#0b1f82" }
                            }}
                        >
                            {t("Sim, fechar")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>}
        </>
    )
}

export default CoafBatchDialog